import React from 'react';
import { Link } from 'react-router-dom';
import { Trash } from '@phosphor-icons/react';

import { Icon } from '../../components/Icon';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';
import { Avatar, QtyStepper, Rating } from '../../components/desktop';
import { Avatar as MobileAvatar, QtyStepper as MobileQtyStepper } from '../../components/mobile';

import './cart.css';

/**
 * The cart pattern: ONE line item, one prop API, four surfaces.
 *
 * WHY THIS IS ONE COMPONENT AND NOT FOUR
 * --------------------------------------
 * The same line appears on `/cart`, in the cart drawer, in the checkout order
 * summary, and in the seller's order-details drawer. The app has three
 * implementations of it today plus a fourth surface that reaches across two
 * directories to import `Cart/CartOverlay/CartOverlay.css`. Adding a fifth is
 * the failure mode this file exists to prevent, so a cart line is built here
 * and nowhere else.
 *
 * A page writes one set of props and gets the desktop track at and above 1024
 * and the mobile track below it. Nothing renders twice: the LAYOUT reflow is a
 * media query on `.dsa-cartline` (identical DOM, fewer columns), and the two
 * things that genuinely differ, the quantity stepper and the remove control,
 * pick a track with `useViewport()` and mount exactly one.
 *
 * ANATOMY ORDER IS FIXED:
 *   thumbnail, set kicker, card name, meta row (condition + card code), unit
 *   price, optional status note, then the trailing group: quantity, line
 *   total, remove.
 *
 * The unit price used to live in the trailing group, stacked directly above
 * the line total. It moved under the name because two Rand amounts of similar
 * width, stacked, right-aligned and unlabelled, are not a price and a total to
 * anyone who did not build them — see the block comment on `.dsa-cartline__unit`
 * in cart.css. Changing this back reintroduces that bug.
 *
 * DOCTRINE THIS ENFORCES, none of it optional:
 * - Prices are RANDS and arrive PRE-FORMATTED with the `R`. This component
 *   never formats currency and must never be handed a dollar amount.
 * - ONE MONEY VALUE PER POSITION. The number under the name is per unit, the
 *   number in the trailing group is what this line costs. A buyer must never
 *   have to work out which is which.
 * - PRICE IS LIGHTER THAN THE NAME. The unit price is lighter again.
 * - There are no PSA / BGS / CGC grades in this product. `condition` is real.
 * - No courier, no origin city and no shipping price is ever named by this
 *   pattern. The Shipping row a caller passes into `CartSummary` is the app's
 *   own arithmetic, see the comment on `SummaryRow`.
 * - There is NO promo-code feature in this app, so there is no code input here
 *   and none may be added. A server-side discount AMOUNT is real, and renders
 *   as an ordinary `SummaryRow`.
 * - There is no "saved for later". The wishlist is the keep-for-later surface.
 * - No "Make an offer" and no "Message seller": neither feature exists.
 */

/* Mobile-track trash glyph. The desktop track uses `.dsk-iconbtn` plus the
   Phosphor `<Icon>`, and neither `dsk-*` nor `.ph` may be emitted below the
   handoff, so the mobile remove control draws its own 24-grid glyph on
   `currentColor`. No colour of its own, no hex. */
function TrashGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The stack a group of lines sits in. */
export function CartLines({ className, ...rest }) {
  return <div className={cn('dsa-cartlines', className)} {...rest} />;
}

/**
 * One cart line.
 *
 * `readOnly` drops the stepper and the remove control and folds the quantity
 * into the price line, which is what the checkout summary and the order drawer
 * need. `compact` is the narrow density for a drawer or a summary column.
 *
 * `max` is the seller's live stock. Passing it disables the plus at the top of
 * the range for real, rather than letting a buyer press it into an error.
 */
export function CartLine({
  image,
  imageAlt = '',
  set,
  name,
  href,
  condition,
  cardNumber,
  note,
  unitPrice,
  quantity,
  max,
  lineTotal,
  onIncrease,
  onDecrease,
  onRemove,
  readOnly = false,
  compact = false,
  unavailable = false,
  removeLabel,
  className,
  ...rest
}) {
  const { isDesktop } = useViewport();
  const canStep = !readOnly && (onIncrease || onDecrease) && !unavailable;
  const canRemove = !readOnly && Boolean(onRemove);

  const NameTag = href ? Link : 'span';
  const nameProps = href ? { to: href } : {};

  /* THE UNIT PRICE IS SUPPRESSED AT QUANTITY 1, and that is the point of it.
     At one unit the per-unit price and the line total are the same number, so
     showing both puts two identical amounts on one narrow line and forces the
     buyer to decide whether they are being charged once or twice. One number
     is unambiguous; two identical numbers are not.

     It still renders when there is no `lineTotal` at all, because then it is
     the only money on the line and suppressing it would show a card with no
     price. */
  const showUnit = Boolean(unitPrice) && (!lineTotal || (quantity !== undefined && quantity > 1));

  return (
    <div
      className={cn(
        'dsa-cartline',
        compact && 'dsa-cartline--compact',
        unavailable && 'dsa-cartline--unavailable',
        className,
      )}
      {...rest}
    >
      <span className="dsa-cartline__thumb">
        {image ? <img src={image} alt={imageAlt} /> : null}
      </span>

      <div className="dsa-cartline__main">
        {set ? <div className="dsa-cartline__kicker">{set}</div> : null}
        <NameTag className="dsa-cartline__name" {...nameProps}>
          {name}
        </NameTag>

        {condition || cardNumber ? (
          <div className="dsa-cartline__meta">
            {condition}
            {cardNumber ? <span className="dsa-cartline__code">{cardNumber}</span> : null}
          </div>
        ) : null}

        {showUnit ? (
          <span className="dsa-cartline__unit">
            {unitPrice}
            {/* A read-only line has no stepper, so the quantity has to be
                carried by the price itself. Where the stepper IS on screen it
                already states the quantity, and repeating it here would be a
                second, quieter, contradictable copy of the same fact. */}
            {readOnly ? (
              /* `quantity` is optional on this component, and a read-only line
                 that was handed none must print nothing rather than
                 "R120 × undefined". */
              quantity === undefined ? null : ` × ${quantity}`
            ) : (
              <span className="dsa-cartline__each">each</span>
            )}
          </span>
        ) : null}

        {note ? <div className="dsa-cartline__note">{note}</div> : null}
      </div>

      <div className="dsa-cartline__end">
        {canStep ? (
          isDesktop ? (
            <QtyStepper
              value={quantity}
              min={1}
              max={max}
              label={`Quantity for ${name}`}
              decreaseLabel={`Decrease quantity for ${name}`}
              increaseLabel={`Increase quantity for ${name}`}
              onChange={(next) => {
                if (next > quantity && onIncrease) onIncrease();
                else if (next < quantity && onDecrease) onDecrease();
              }}
            />
          ) : (
            <MobileQtyStepper
              value={quantity}
              min={1}
              max={max}
              decrementLabel={`Decrease quantity for ${name}`}
              incrementLabel={`Increase quantity for ${name}`}
              onDecrement={onDecrease}
              onIncrement={onIncrease}
            />
          )
        ) : null}

        {/* AFTER the stepper, not before it. The row reads left to right as
            "how many" then "what that costs", which is the order a buyer
            changes them in: press plus, watch this number move. */}
        {lineTotal ? <span className="dsa-cartline__total">{lineTotal}</span> : null}

        {canRemove ? (
          isDesktop ? (
            <button
              type="button"
              className="dsk-iconbtn dsa-cartline__remove"
              aria-label={removeLabel || `Remove ${name} from cart`}
              onClick={onRemove}
            >
              <Icon as={Trash} size="sm" />
            </button>
          ) : (
            <button
              type="button"
              className="dsa-cartline__remove"
              aria-label={removeLabel || `Remove ${name} from cart`}
              onClick={onRemove}
            >
              <TrashGlyph />
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}

/**
 * A seller group: who supplies these lines, and what they come to.
 *
 * The sub line is deliberately generic. The standing product rule is that we
 * never name a courier and never name a city of origin, because the app cannot
 * pull either, so this says "Delivered nationwide" and nothing more.
 */
export function CartSellerGroup({
  storeName,
  storeHref,
  rating,
  sub = 'Delivered nationwide across South Africa',
  action,
  footer,
  children,
  className,
  ...rest
}) {
  const { isDesktop } = useViewport();
  const TrackAvatar = isDesktop ? Avatar : MobileAvatar;
  const NameTag = storeHref ? Link : 'span';
  const nameProps = storeHref ? { to: storeHref } : {};

  // Both tracks' avatars are initials discs, but only the desktop one derives
  // them from a name, so the derivation is done once here and handed to both
  // as children.
  const initials = String(storeName || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <section className={cn('dsa-cartgroup', className)} {...rest}>
      <header className="dsa-cartgroup__head">
        <TrackAvatar>{initials}</TrackAvatar>

        <div className="dsa-cartgroup__heading">
          <div className="dsa-cartgroup__name">
            <NameTag {...nameProps}>{storeName}</NameTag>
            {rating > 0 ? (
              isDesktop ? (
                <Rating value={Number(rating)} size="sm" showValue />
              ) : (
                <span className="dsa-cartgroup__score">{Number(rating).toFixed(1)}/5</span>
              )
            ) : null}
          </div>
          {sub ? <p className="dsa-cartgroup__sub">{sub}</p> : null}
        </div>

        {action ? <div className="dsa-cartgroup__action">{action}</div> : null}
      </header>

      {children}

      {footer ? <div className="dsa-cartgroup__foot">{footer}</div> : null}
    </section>
  );
}

/** The label/value pair inside a group footer. */
export function GroupTotal({ label, value }) {
  return (
    <div className="dsa-cartgroup__foot-total">
      <span className="dsa-cartgroup__foot-label">{label}</span>
      <span className="dsa-cartgroup__foot-value">{value}</span>
    </div>
  );
}

/**
 * One row of an order summary.
 *
 * Nothing about this row invents a fact. A caller passes the label and the
 * pre-formatted Rand amount it already has; if the app has no number for a row
 * it does not render the row.
 */
export function SummaryRow({ label, value, sub = false, className, ...rest }) {
  return (
    <div className={cn('dsa-sumrow', sub && 'dsa-sumrow--sub', className)} {...rest}>
      <span className="dsa-sumrow__label">{label}</span>
      <span className="dsa-sumrow__value">{value}</span>
    </div>
  );
}

/**
 * Prose under a summary. NOT a row.
 *
 * `SummaryRow` is a label/value pair and its right column is a money column:
 * every other row in the block puts a Rand amount there. Anything that is not
 * an amount — "Calculated at checkout", "Each seller is checked out
 * separately" — must come through here instead, or it borrows the authority of
 * a price it is not.
 *
 * The test is blunt: if you cannot add it to the subtotal, it is not a value,
 * so it does not get a value slot.
 */
export function SummaryNote({ children, className, ...rest }) {
  return (
    <p className={cn('dsa-sumnote', className)} {...rest}>
      {children}
    </p>
  );
}

/** The bold sum line. `currency` renders the quiet ZAR marker beside "Total". */
export function SummaryTotal({ label = 'Total', currency = 'ZAR', value, className, ...rest }) {
  return (
    <div className={cn('dsa-sumtotal', className)} {...rest}>
      <span>
        {label} {currency ? <span className="dsa-sumtotal__cur">{currency}</span> : null}
      </span>
      <span>{value}</span>
    </div>
  );
}

/**
 * The summary surface. `sticky` pins it beside a long item list on desktop,
 * and it is only correct when this card IS the whole column: sticky offsets an
 * element inside its parent, so anything stacked under it in the same wrapper
 * gets covered as the page scrolls. Checkout hit exactly that with its Place
 * Order block and now pins the wrapper instead — see `.dsa-checkout__aside`.
 */
export function CartSummary({ title, action, sticky = false, children, className, ...rest }) {
  return (
    <aside
      className={cn('dsa-cartsummary', sticky && 'dsa-cartsummary--sticky', className)}
      {...rest}
    >
      {title ? (
        action ? (
          <div className="dsa-cartsummary__head">
            <h2 className="dsa-cartsummary__title">{title}</h2>
            {action}
          </div>
        ) : (
          <h2 className="dsa-cartsummary__title">{title}</h2>
        )
      ) : null}
      {children}
    </aside>
  );
}

/** The thumbnail strip above a condensed summary, checkout.html `.pg-thumbs`. */
export function SummaryThumbs({ items = [], className, ...rest }) {
  if (items.length === 0) return null;
  return (
    <div className={cn('dsa-sumthumbs', className)} {...rest}>
      {items.map((item, index) => (
        <span className="dsa-sumthumbs__item" key={item.key ?? index}>
          {item.image ? <img src={item.image} alt={item.alt || ''} /> : null}
        </span>
      ))}
    </div>
  );
}

/** The quiet "N items from M sellers" line under the thumbnails. */
export function SummaryCount({ children, className, ...rest }) {
  return (
    <p className={cn('dsa-sumcount', className)} {...rest}>
      {children}
    </p>
  );
}

/**
 * The two-column split shared by the cart page and checkout. Desktop only:
 * below the handoff the summary simply follows the items in one column.
 */
export function CartLayout({ variant = 'cart', className, ...rest }) {
  return (
    <div
      className={cn('dsa-cartlayout', variant === 'checkout' && 'dsa-cartlayout--checkout', className)}
      {...rest}
    />
  );
}

export default CartLine;
